// Auth wire client (ADR 0039 §6.2 as refined by P6-3: server-driven PKCE).
// The client carries NO crypto: login starts with a top-level navigation to
// the API's /start route (so the txn cookie is set first-party), and the
// callback page just posts {provider, code, state} back. Sessions live in an
// httpOnly cookie; "who am I" is always asked of the server.

import { ApiRequestError, createApiClient, type ApiClient } from './api-client.js';

export type SocialProvider = 'google' | 'discord';

export interface SessionInfo {
  readonly accountId: string;
  readonly email?: string;
  readonly provider: string;
}

export interface AuthClient {
  /** Top-level navigation target that begins the OIDC flow. */
  loginUrl(provider: SocialProvider): string;
  /** Current session, or null when not signed in (401). */
  session(): Promise<SessionInfo | null>;
  /** Completes the callback leg; the session cookie is set by the response. */
  completeLogin(provider: string, code: string, state: string): Promise<SessionInfo>;
  logout(): Promise<void>;
}

export function createAuthClient(baseUrl: string, api: ApiClient = createApiClient(baseUrl)): AuthClient {
  return {
    loginUrl: (provider) => `${baseUrl}/v1/auth/oidc/${provider}/start`,
    async session() {
      try {
        const response = await api.get('/v1/auth/session');
        return (await response.json()) as SessionInfo;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
    async completeLogin(provider, code, state) {
      const response = await api.post('/v1/auth/login', { code, provider, state });
      const body = (await response.json()) as { accountId: string; email: string | null; provider: string };
      return {
        accountId: body.accountId,
        provider: body.provider,
        ...(body.email === null ? {} : { email: body.email }),
      };
    },
    async logout() {
      await api.post('/v1/auth/logout');
    },
  };
}
